import { Add, Topic } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Hidden,
  Icon,
  TextField,
  Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import React from 'react'
import { connect } from 'react-redux'
import DocumentAdminFileCard from 'components/admin/docs-admin-file-card'
import { DocumentFileViewer } from 'components/documents'
import UploadNewFile from 'components/documents/upload-prompt'
import { NivosModal } from 'components/index'
import {
  Operations as DocumentAdminOperations,
  operations as documentAdminOperations
} from 'states/dux/document-admin/operations'
import { Payload as DocumentAdminPayload } from 'states/dux/document-admin/types'
import {
  Operations as DocumentOperations,
  operations as documentOperations
} from 'states/dux/document/operations'
import { Payload as DocumentPayload } from 'states/dux/document/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserAdminOperations,
  operations as userAdminOperations
} from 'states/dux/user-admin/operations'
import { Payload as UserAdminPayload } from 'states/dux/user-admin/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosFile } from 'types/nivos-file'
import { NivosFolder } from 'types/nivos-folder'
import { NivosUser, NivosUserGroup } from 'types/user'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  userAdmin: UserAdminPayload
  document: DocumentPayload
  documentAdmin: DocumentAdminPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    userAdmin: UserAdminOperations
    document: DocumentOperations
    documentAdmin: DocumentAdminOperations
  }
}

interface OwnProps {
  selectedFolder: NivosFolder | undefined
  setSelectedFolder: (folder: NivosFolder | undefined) => void
}

interface SelectFieldOptions {
  label: string
  value: number
}

type AppProps = StateProps & DispatchProps & OwnProps

const DocsAdminFolderModalFC: React.FC<any> = (props: AppProps) => {
  const { documentAdmin, selectedFolder, operations, setSelectedFolder } = props

  const emptyFolderData = {
    id: 0,
    name: '',
    groupsAccess: [] as number[],
    usersAccess: [] as number[],
    prohibitedUsers: [] as number[],
    files: [] as NivosFile[]
  }

  const [groupSelection, setGroupSelection] = React.useState<
  SelectFieldOptions[]
  >([])

  const [userSelection, setUserSelection] = React.useState<
  SelectFieldOptions[]
  >([])

  const [prohibitedUserSelection, setProhibitedUserSelection] = React.useState<
  SelectFieldOptions[]
  >([])

  const [groupSelectOption, setGroupOption] = React.useState<
  SelectFieldOptions[]
  >([])

  const [userSelectOption, setUserOption] = React.useState<
  SelectFieldOptions[]
  >([])

  const [uploadOpen, setUploadOpen] = React.useState(false)
  const [modalLoading, setModalLoading] = React.useState(false)
  const [folderEditData, setEditData] = React.useState(emptyFolderData)
  const isMobileView = useMediaQuery('(max-width:600px)')

  const [errors, setErrors] = React.useState({
    name: false,
    accessGroups: false,
    accessUsers: false,
    prohibitedUsers: false
  })

  const _updateFolder = (): void => {
    setModalLoading(true)
    if (folderEditData.id !== undefined && folderEditData.id !== 0) {
      const updateData: any = {}

      updateData.name = folderEditData.name
      updateData.users_access = folderEditData.usersAccess
      updateData.groups_access = folderEditData.groupsAccess
      updateData.prohibited_users = folderEditData.prohibitedUsers

      void post(
        `/document/${folderEditData?.id}/`,
        updateData,
        false,
        false,
        'PUT'
      )
        .then(data => {
          try {
            operations.documentAdmin.update(objectifyDateStringFields(data))
          } catch (e) {
            console.log(e)
          }
        })
        .finally(() => {
          setEditData(emptyFolderData)
          setSelectedFolder(undefined)
          setModalLoading(false)
        })
    } else if (folderEditData.id === 0) {
      const requiredFields = ['name']
      let errorStates = errors
      let hasErrors = false
      Object.entries(folderEditData).forEach(entry => {
        if (
          requiredFields.includes(entry[0]) &&
          (entry[1] === '' ||
            entry[1] === null ||
            entry[1] === undefined ||
            (Array.isArray(entry[1]) && entry[1].length === 0))
        ) {
          errorStates = { ...errorStates, [entry[0]]: true }
          hasErrors = true
        }
      })
      setErrors(errorStates)
      if (hasErrors) {
        setModalLoading(false)
        return
      }
      void post('/document/', {
        name: folderEditData.name,
        users_access: folderEditData.usersAccess,
        groups_access: folderEditData.groupsAccess,
        prohibited_users: folderEditData.prohibitedUsers
      })
        .then(data => {
          try {
            operations.documentAdmin.add(objectifyDateStringFields(data))
          } catch (e) {
            console.log(e)
          }
        })
        .finally(() => {
          setEditData(emptyFolderData)
          setSelectedFolder(undefined)
          setModalLoading(false)
        })
    }
  }

  React.useEffect(() => {
    void get('/account/').then((data: NivosUser[]) => {
      try {
        setUserOption(
          data.map(item => ({ label: item.fullName, value: item.id }))
        )
      } catch (e) {
        console.log(e)
      }
    })
    void get('/account/group/').then((data: NivosUserGroup[]) => {
      try {
        setGroupOption(data.map(item => ({ label: item.name, value: item.id })))
      } catch (e) {
        console.log(e)
      }
    })
  }, [])

  React.useEffect(() => {
    if (selectedFolder !== undefined) {
      // TODO: Initialize with selectedFolder data
      const groupsAccess = selectedFolder.groupsAccess.map(item => item.id)
      const usersAccess = selectedFolder.usersAccess.map(item => item.id)
      const prohibitedUsers = selectedFolder.prohibitedUsers.map(
        item => item.id
      )

      setEditData({
        id: selectedFolder.id,
        name: selectedFolder.name,
        usersAccess,
        groupsAccess,
        prohibitedUsers,
        files: selectedFolder.files
      })

      setGroupSelection(
        groupSelectOption.filter(item => groupsAccess.includes(item.value))
      )
      setUserSelection(
        userSelectOption.filter(item => usersAccess.includes(item.value))
      )
      setProhibitedUserSelection(
        userSelectOption.filter(item => prohibitedUsers.includes(item.value))
      )
    }
  }, [selectedFolder])

  React.useEffect(() => {
    // Track selected folder details upon folder updates on react-redux
    if (selectedFolder !== undefined) {
      setSelectedFolder(
        documentAdmin.data.filter(folder => folder.id === selectedFolder.id)[0]
      )
    }
  }, [documentAdmin])

  return (
    <NivosModal
      title={selectedFolder?.id !== 0 ? 'Manage Folder' : 'Create Folder'}
      open={selectedFolder !== undefined}
      setOpen={_ => setSelectedFolder(undefined)}
      containerProps={{
        sx: {
          height: isMobileView ? '100vh' : 'fit-content',
          padding: isMobileView ? 0 : 3,
          borderRadius: isMobileView ? 0 : 4,
          margin: 0
        }
      }}
    >
      {selectedFolder !== undefined && (
        <Box
          display={'flex'}
          alignItems={'center'}
          flexDirection={isMobileView ? 'column' : 'row'}
          width={isMobileView ? '100vw' : folderEditData.id !== 0 ? 700 : 400}
          height={1}
        >
          <Box display={'flex'} width={1}>
            <Box width={1} padding={1} height={1}>
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: (
                    <Icon sx={{ marginRight: '5px' }}>
                      <Topic />
                    </Icon>
                  )
                }}
                onChange={event => {
                  setErrors({ ...errors, name: false })
                  setEditData({
                    ...folderEditData,
                    name: event.currentTarget.value
                  })
                }}
                error={errors.name}
                id={'f-name'}
                placeholder={'Folder name'}
                defaultValue={selectedFolder.name}
                sx={{ marginY: 1 }}
                disabled={modalLoading}
              />
              <Box paddingY={1} width={1}>
                <Autocomplete
                  disablePortal
                  multiple={true}
                  options={groupSelectOption}
                  value={groupSelection}
                  onChange={(event, value) => {
                    setGroupSelection(value)
                    setEditData({
                      ...folderEditData,
                      groupsAccess: value.map(x => x.value)
                    })
                    setErrors({ ...errors, accessGroups: false })
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={errors.accessGroups}
                      label="Access ( Groups )"
                    />
                  )}
                />
              </Box>
              <Box paddingY={1} width={1}>
                <Autocomplete
                  disablePortal
                  multiple
                  options={userSelectOption}
                  value={userSelection}
                  onChange={(event, value) => {
                    setUserSelection(value)
                    setEditData({
                      ...folderEditData,
                      usersAccess: value.map(x => x.value)
                    })
                    setErrors({ ...errors, accessUsers: false })
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={errors.accessUsers}
                      label="Access ( Users )"
                    />
                  )}
                />
              </Box>
              <Box paddingY={1} width={1}>
                <Autocomplete
                  disablePortal
                  multiple
                  options={userSelectOption}
                  value={prohibitedUserSelection}
                  onChange={(event, value) => {
                    setProhibitedUserSelection(value)
                    setEditData({
                      ...folderEditData,
                      prohibitedUsers: value.map(x => x.value)
                    })
                    setErrors({ ...errors, prohibitedUsers: false })
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={errors.prohibitedUsers}
                      label="Prohibited ( Users )"
                    />
                  )}
                />
              </Box>
              <Hidden smDown>
                <Box
                  display={'flex'}
                  justifyContent={'flex-end'}
                  paddingTop={5}
                >
                  <Button
                    variant={'contained'}
                    size={'large'}
                    sx={{ marginX: 1 }}
                    onClick={() => _updateFolder()}
                    disabled={modalLoading}
                  >
                    {modalLoading && 'Loading..'}
                    {!modalLoading &&
                      (folderEditData.id !== 0 ? 'Save' : 'Create')}
                  </Button>
                  <Button
                    variant={'outlined'}
                    size={'large'}
                    sx={{ marginX: 1 }}
                    onClick={() => setSelectedFolder(undefined)}
                  >
                    Cancel
                  </Button>
                </Box>
              </Hidden>
            </Box>
          </Box>
          {folderEditData.id !== 0 && (
            <Box
              width={isMobileView ? '85vw' : 900}
              height={isMobileView ? 250 : 360}
              overflow={'auto'}
              border={1}
              borderRadius={4}
              padding={2}
              marginLeft={1}
            >
              <DocumentFileViewer />
              <Box display={'flex'} flexWrap={'wrap'}>
                {folderEditData.files.map((item, index) => (
                  <DocumentAdminFileCard
                    key={index}
                    fileDetails={item}
                    folderDetails={selectedFolder}
                    onClickEvent={_ => operations.document.setSelected(_)}
                  />
                ))}
                <Box
                  height={'fit-content'}
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  onClick={() => setUploadOpen(true)}
                  sx={{
                    cursor: 'pointer',
                    marginY: 2,
                    paddingX: 1,
                    transition: '0.5s',
                    ':hover': {
                      marginTop: 0
                    }
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 30,
                      transition: '0.5s'
                    }}
                  >
                    <Add fontSize={'inherit'} />
                  </Icon>
                  <Typography>Upload File</Typography>
                </Box>
              </Box>
            </Box>
          )}
          <Hidden smUp>
            <Box display={'flex'} justifyContent={'flex-end'} paddingTop={5}>
              <Button
                variant={'contained'}
                size={'large'}
                sx={{ marginX: 1 }}
                onClick={() => _updateFolder()}
                disabled={modalLoading}
              >
                {modalLoading && 'Loading..'}
                {!modalLoading && (folderEditData.id !== 0 ? 'Save' : 'Create')}
              </Button>
              <Button
                variant={'outlined'}
                size={'large'}
                sx={{ marginX: 1 }}
                onClick={() => setSelectedFolder(undefined)}
              >
                Cancel
              </Button>
            </Box>
          </Hidden>
          {folderEditData.id !== 0 && (
            <UploadNewFile
              folder={selectedFolder}
              open={uploadOpen}
              setOpen={setUploadOpen}
              isAdmin={true}
            />
          )}
        </Box>
      )}
    </NivosModal>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    userAdmin: state.userAdmin,
    document: state.document,
    documentAdmin: state.documentAdmin
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  userAdmin: userAdminOperations,
  document: documentOperations,
  documentAdmin: documentAdminOperations
})

const DocsAdminFolderModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(DocsAdminFolderModalFC) as React.FC<OwnProps>

export default DocsAdminFolderModal
